import React from "react";
import { api } from "./api.jsx";
import { track } from "./analytics.jsx";
import { useApp } from "./store.jsx";
import { Dialog } from "./chrome.jsx";

const { Button } = window.CardboardmarketDesignSystem_2a3239;

/**
 * "That is not this card."
 *
 * WHERE IT LIVES, AND WHY IT MOVED.
 *
 * It went in on the listing page first, which was the wrong instinct and the
 * owner said so plainly: that surface is BUYER-facing, and a buyer is the person
 * least able to tell that a picture is wrong. They are looking at a card they do
 * not own.
 *
 * The person who knows is the SELLER, at the moment they are posting: they have
 * the card in their hand, the catalogue has just told them what it thinks the
 * card looks like, and the two disagree. That is the whole report, and it is
 * available a screen earlier than we were asking for it. So this renders in both
 * places, and the posting form is the one that matters.
 *
 * WHY THIS IS ON THE PICTURE AND NOT IN THE FEEDBACK FORM. The person who can
 * tell that a catalogue picture is wrong is looking at the card while they think
 * it — and a general "report a problem" box asks them to describe which card,
 * which printing and which picture in prose. One tap on the thing itself carries
 * all three exactly.
 *
 * WHAT IT IS NOT ABOUT. Not the listing, and not the seller. The picture is
 * OURS, so a wrong one is our fault and it is wrong everywhere that picture is
 * shown. Filing it against the listing would put a mark next to somebody who did
 * nothing but sell the card, and would vanish when they took it down.
 *
 * SO IT ONLY APPEARS OVER THE CATALOGUE'S OWN PICTURE. A seller's photograph is
 * of the card in their hand; there is nothing for us to correct and nothing this
 * report would mean.
 *
 * A CARD CODE OR A PRODUCT, AND THAT IS THE WHOLE OF THIS COMPONENT'S HISTORY.
 * It used to require `cardCode`, which read as "off-catalogue listings have no
 * row to fix" and was true — but the code is also the one identifier four of the
 * five games do not have. 11,850 of One Piece's 12,421 listable products carry
 * one; Magic, Yu-Gi-Oh, Pokemon and Lorcana carry ZERO between them. So this
 * button never appeared for 285,000 products, and nobody had decided that.
 *
 * It was invisible rather than harmful while those games had no reliable picture
 * to be wrong about. They have one per printing now, so either identifier will
 * do: a product names a printing, which is what a picture belongs to.
 */
export function PictureReport({ game, cardCode, productId, kind }) {
  const { t } = useApp();
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [state, setState] = React.useState('idle');

  // Nothing in our catalogue to be wrong about. Either identifier names
  // something we can fix; neither means there is nothing to fix.
  if (!cardCode && !productId) return null;

  const send = async () => {
    setState('sending');
    try {
      await api.reportCardImage({
        game,
        cardCode: cardCode || undefined,
        sourceProductId: cardCode ? undefined : productId,
        kind,
        note: note.trim() || undefined,
      });
      /*
       * Thanked whether or not this was their second attempt.
       *
       * The server answers a duplicate with the report they already made rather
       * than an error, so there is no failure to render here — and a person who
       * taps twice has done nothing that deserves a red box.
       */
      setState('sent');
      track('card_image_reported', { value: kind === 'missing' ? 0 : 1 });
    } catch (e) {
      setState('failed');
    }
  };

  if (state === 'sent') {
    return (
      <p role="status" style={{ font: 'var(--type-meta)', color: 'var(--text-faint)', margin: 0 }}>
        {t("Thank you \u2014 we will check this picture.")}
      </p>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        /* Quiet on purpose: it sits under every catalogue picture on the site,
           and a picture being wrong is the rare case. Loud enough to find when
           you are already frowning at the card, never loud enough to suggest
           doubt about a picture that is fine. */
        style={{
          font: 'var(--type-meta)', color: 'var(--text-faint)', background: 'none',
          border: 0, padding: 0, cursor: 'pointer', textDecoration: 'underline',
          textUnderlineOffset: 3, alignSelf: 'flex-start',
        }}
      >
        {kind === 'missing' ? t("Tell us the picture is missing") : t("This is not the right picture")}
      </button>
      {open ? (
        <Dialog
          open
          width={440}
          closeLabel={t("Close")}
          onClose={() => setOpen(false)}
          title={kind === 'missing' ? t("No picture for this card") : t("Wrong picture for this card")}
          description={kind === 'missing'
            ? (cardCode
              ? t("We have no picture for {code}. Telling us means somebody can find one.", { code: cardCode })
              : t("We have no picture for this printing. Telling us means somebody can find one."))
            : (cardCode
              ? t("The picture we show for {code} does not match the card. Tell us and somebody will look.", { code: cardCode })
              : t("The picture we show for this printing does not match the card. Tell us and somebody will look."))}
          /*
           * THE BUTTONS BELONG IN THE FOOTER, which is what every other dialog in
           * this product does and what the design system's own spacing is built
           * around. They were inside the body, directly under the textarea with
           * nothing between them — reported with a screenshot, and it was the
           * design system being ignored rather than a missing margin.
           */
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t("Cancel")}</Button>
              <Button onClick={send} disabled={state === 'sending'}>
                {state === 'sending' ? t("Sending\u2026") : t("Send")}
              </Button>
            </>
          }
        >
          {/*
            AND THE BODY GETS THE STANDARD GAP. A dialog's children are stacked
            with no space of their own — every other one wraps them like this, at
            `--space-5`, which is the layout rhythm's default.
          */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              {t("Anything that helps (optional)")}
            </span>
            {/* Optional, and last. Asking somebody to explain before they can
                report is how a one-tap report becomes a form nobody finishes. */}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder={t("For example: this is the alternate art, mine has the normal border.")}
              style={{
                font: 'var(--type-body)', color: 'var(--text-default)',
                padding: 'var(--space-3)', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-hairline)', background: 'var(--white)',
                resize: 'vertical',
              }}
            />
          </label>
          {state === 'failed' ? (
            <p role="alert" style={{ font: 'var(--type-meta)', color: 'var(--danger-600)', margin: 0 }}>
              {t("That did not send. Try again in a moment.")}
            </p>
          ) : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
